/**
 * THE SESSION LEDGER — every signal evaluated since the dialog was first opened.
 *
 * <h2>It computes nothing</h2>
 *
 * Win rate, profit factor and the cumulative return all come from
 * {@link calculatePortfolioMetrics}, which is the one place portfolio arithmetic happens in this
 * application. A second accumulator here would be the failure this repository has already had
 * once between the Running Ledger and the Simulator — one summing percentages, one compounding
 * them — in the worst possible place: two numbers about the same trades, a centimetre apart.
 *
 * <h2>Keyed by signal, not by click</h2>
 *
 * Clicking the same point twice re-selects it and does not add a second trade. A theoretical
 * outcome is a property of the bar, so counting it twice would inflate every rate in the panel
 * while the chart showed one mark. The CLICK count is kept separately, so "signals clicked" and
 * "signals evaluated" are both answerable and a re-click is visible rather than silently lost.
 *
 * <h2>Order is load-bearing</h2>
 *
 * Trades are held in CLICK order, not bar order. Drawdown is path-dependent and the session is
 * the reader's own path through the chart — re-sorting by date would report a drawdown they
 * never experienced. Stated because the temptation to sort is real: the dialog lists them.
 */

import { calculatePortfolioMetrics } from './portfolioMetrics';
import { OUTCOMES } from './tradeSimEngine';
import { institutionalTelemetry } from './tradeSimMetrics';

/** A fresh session. Frozen, so a caller cannot mutate the shared empty state into a used one. */
export const EMPTY_SESSION = Object.freeze({
  trades: [],
  clicks: 0,
  selectedId: null,
});

/** `CTS:2024-03-07` — unique per signal and stable across re-clicks. */
export const signalId = (provider, date) => `${provider || '?'}:${date || '?'}`;

/**
 * Records a click.
 *
 * <p>An UNPRICED trade (no ATR at that bar) is still recorded and still selected: the card has
 * to be able to say why it cannot evaluate this one, and dropping it would make the click look
 * like it had missed the marker.
 *
 * @param {object} session the previous session
 * @param {object} trade   a `simulateTrade` result, plus `provider`, `date` and `signal`
 */
export function recordClick(session, trade) {
  const prev = session || EMPTY_SESSION;
  if (!trade || !trade.provider || !trade.date) return prev;
  /*
   * AN EXPLICIT ID WINS, and that is how the two families differ about what a duplicate IS.
   *
   * For CTS a theoretical outcome is a property of the bar, so clicking the same point twice is
   * the same trade and must not be counted twice. A MANUAL trade is an EXECUTION the reader
   * chose: a long and a short opened on the same bar of the same channel are two trades, and so
   * is the same round trip taken twice. Found in a browser — without this a short taken over a
   * long's own legs silently REPLACED it and the ledger read "2 clicked, 1 evaluated".
   */
  const id = trade.executionId || signalId(trade.provider, trade.date);
  const existing = prev.trades.findIndex(t => t.id === id);
  if (existing >= 0) {
    // A re-click SELECTS and does not re-add. The stored trade is replaced rather than kept,
    // because the chart may have been redrawn with more bars since — an OPEN trade can have
    // resolved, and showing the stale card would be the one case where a reader is told a
    // result is pending when it is not.
    const trades = prev.trades.slice();
    trades[existing] = { ...trade, id };
    return { ...prev, trades, clicks: prev.clicks + 1, selectedId: id };
  }
  return {
    ...prev,
    trades: [...prev.trades, { ...trade, id }],
    clicks: prev.clicks + 1,
    selectedId: id,
  };
}

/**
 * Replaces a stored trade IN PLACE, without counting a click.
 *
 * <p>The replay re-prices a gated trade on every bar the playhead advances, so by the time it
 * resolves the ledger must hold the RESOLVED record rather than the OPEN one it was taken as.
 * That is not a click and must not be counted as one: `clicks` is what distinguishes "the
 * reader pressed the same marker four times" from "four signals were taken", and a boundary
 * step is neither.
 *
 * <p>It also leaves the SELECTION alone. A reader watching one card while the playhead advances
 * a different trade must not have the card swapped underneath them.
 *
 * @returns the session unchanged when no stored trade matches, so a stale gate cannot insert one
 */
export function repriceTrade(session, trade) {
  const prev = session || EMPTY_SESSION;
  if (!trade) return prev;
  const id = trade.executionId || signalId(trade.provider, trade.date);
  const at = prev.trades.findIndex(t => t.id === id);
  if (at < 0) return prev;
  const trades = prev.trades.slice();
  /*
   * MERGED ONTO THE STORED ENTRY, not substituted for it.
   *
   * FOUND IN A BROWSER. A re-price comes from `simulateTrade`, which knows the bars and the
   * rules and nothing else — it carries no `signal`, so replacing the entry wholesale threw
   * away the odds record the card's whole top half is built from. The SIGNAL section rendered
   * its heading over nothing: no direction, no odds, no regime, on a card that had shown all
   * three a moment earlier. Every field the engine recomputes is overwritten here; the ones it
   * has never heard of are the identity and the context, which a re-price cannot change.
   */
  trades[at] = { ...trades[at], ...trade, id };
  return { ...prev, trades };
}

/** Clears the ledger and the selection. The dialog's own Reset. */
export const clearSession = () => ({ ...EMPTY_SESSION });

/** The trade the card is showing, or null. */
export const selectedTrade = (session) => {
  const s = session || EMPTY_SESSION;
  return s.trades.find(t => t.id === s.selectedId) || null;
};

/**
 * The rolling summary.
 *
 * <h2>What counts, and what does not</h2>
 *
 * <ul>
 *   <li><b>OPEN trades are excluded from every rate.</b> A signal whose horizon runs past the
 *       last bar has no outcome; folding its mark-to-market into a win rate would make every
 *       recent click look flat for the sole reason that time has not passed. The same rule the
 *       odds engine applies to an unresolved precedent, and the same rule the Running Ledger
 *       applies to an open position.</li>
 *   <li><b>UNPRICED trades are excluded too</b>, and counted separately. "Could not be priced"
 *       and "resolved at zero" are different facts.</li>
 *   <li><b>MEAN R is withheld unless EVERY realised trade carries one.</b> A manual trade has
 *       no stop, so it has no R — and `accumulateTrade` counts a missing one as zero over the
 *       full trade count, so a session mixing CTS and channel trades would report a mean R
 *       diluted by trades that never had one. `rMeasured` is what the panel gates on.</li>
 *   <li><b>A NEUTRAL expiry is NOT excluded.</b> It has a realised P&L — the trade was closed at
 *       the horizon bar — so it counts, and it counts as a win or a loss by that P&L exactly as
 *       `calculatePortfolioMetrics` scores any other trade (`pnlPercent > 0` strictly). Its
 *       three-way OUTCOME label is about which rule ended it, which is a different question
 *       from whether it made money, and the panel reports both.</li>
 * </ul>
 */
export function sessionSummary(session) {
  const s = session || EMPTY_SESSION;
  const priced = s.trades.filter(t => t.priced);
  const realised = priced.filter(t => t.outcome !== OUTCOMES.OPEN);
  const metrics = calculatePortfolioMetrics(realised);
  /*
   * THE INSTITUTIONAL HALF, from its own module and over the SAME realised set.
   *
   * Delegated rather than accumulated here for the reason the rest of this function is: two
   * implementations of one quantity is how the Running Ledger and the Simulator came to report
   * different Net P&L for the same trades. `calculatePortfolioMetrics` owns everything it
   * already provides; `institutionalTelemetry` owns the excursions, the benchmark and the
   * R-denominated payoff, which are genuinely new quantities rather than renamings.
   */
  const inst = institutionalTelemetry(realised);
  const count = (outcome) => realised.filter(t => t.outcome === outcome).length;
  return {
    clicks: s.clicks,
    evaluated: s.trades.length,
    realised: realised.length,
    open: priced.length - realised.length,
    unpriced: s.trades.length - priced.length,
    wins: count(OUTCOMES.WIN),
    losses: count(OUTCOMES.LOSS),
    neutral: count(OUTCOMES.NEUTRAL),
    // Straight from the one implementation. Null profit factor when nothing lost — an absent
    // denominator, not a perfect score.
    winRatePercent: metrics.winRatePercent,
    profitFactor: metrics.profitFactor,
    // ── the institutional telemetry, spread rather than re-listed so a field added there
    //    reaches the panel without a second edit here ───────────────────────────────────────
    ...inst,
    // The PERCENT payoff from the one implementation, beside the R-denominated one above.
    // Two different questions: how big the winners were, and how big they were per unit of
    // the volatility they were taken in.
    payoffRatio: metrics.payoffRatio,
    averageRewardToRisk: metrics.averageRewardToRisk,
    totalNetPnlPercent: metrics.totalNetPnlPercent,
    averageRMultiple: metrics.averageRMultiple,
    // How many of the realised trades actually HAVE an R. Equal to `realised` means the mean
    // above is over all of them; anything less and the panel says n/a rather than a figure
    // dragged toward zero by trades with no stop.
    rMeasured: realised.filter(t => Number.isFinite(t.rMultiple)).length,
    maxDrawdownPercent: metrics.maxDrawdownPercent,
    // SUMMED, because points are currency rather than a rate — the same distinction
    // calculatePortfolioMetrics draws for `totalGrossPoints`.
    totalPoints: realised.reduce((a, t) => a + (Number.isFinite(t.pnl) ? t.pnl : 0), 0),
  };
}

export default recordClick;
